import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { EARN_CREDITS_OFFER_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the `symphonyOutOfCreditsEarnOfferEnabled` gate — the ONE switch behind the
 * out-of-credits "Earn free credits" offer, on all three of its entry points.
 *
 * It FAILS CLOSED: an unreachable route, a non-200, or any conduct failure resolves to `false`, so
 * the offer stays dark until the release is switched to treatment. That direction is the point —
 * the offer sits beside Upgrade, and an accidental always-on would eat conversion we cannot measure
 * our way back out of. It is therefore a ramp AND a kill switch, unlike the voice OFF switch next
 * door (which ships on and only ever turns off).
 *
 * A dev-tools **Feature toggles** override wins over the conducted value, so forcing it On/Off
 * exercises the real flow rather than a special case. That override is inert without a dev-tools
 * grant (lib/dev-feature-toggles.ts), so a real user always gets the conducted value.
 */

let cached: Promise<boolean> | null = null;

export function earnCreditsOfferEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(EARN_CREDITS_OFFER_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/earn-credits-offer`, { credentials: 'include' });
      if (!res.ok) return false;
      return ((await res.json()) as boolean) === true;
    } catch {
      return false;
    }
  })();
  return cached;
}

/**
 * Resolve the gate for rendering. Starts false so the offer never flashes in and then out for a
 * user it is not ramped to; the enabled answer arrives a moment later.
 */
export function useEarnCreditsOfferEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    void earnCreditsOfferEnabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
